import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { useAuth } from '../hooks/useAuth'
import { CampaignMessage, CampaignPollResults, getCampaignPollResults, getCampaignPolls } from '../lib/api/campaignMessages'

const PAGE_SIZE = 50

const formatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : '—'

export function CampaignPollResultsPage() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const selectedId = params.get('campaign')
  const [polls, setPolls] = useState<CampaignMessage[]>([])
  const [results, setResults] = useState<CampaignPollResults | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    getCampaignPolls(0, PAGE_SIZE).then((page) => {
      if (!active) return
      setPolls(page)
      setHasMore(page.length === PAGE_SIZE)
    }).catch(() => { if (active) setError('Could not load polls.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (!selectedId) { setResults(null); return }
    let active = true
    setResults(null)
    setError(null)
    getCampaignPollResults(selectedId).then((data) => { if (active) setResults(data) })
      .catch(() => { if (active) setError('Could not load poll results.') })
    return () => { active = false }
  }, [selectedId])

  const loadMore = async () => {
    setLoadingMore(true)
    try {
      const page = await getCampaignPolls(polls.length, PAGE_SIZE)
      setPolls((current) => [...current, ...page])
      setHasMore(page.length === PAGE_SIZE)
    } catch { setError('Could not load older polls.') }
    finally { setLoadingMore(false) }
  }

  if (user?.role !== 'Admin' && user?.role !== 'Editor') return <p>Poll results are available to Admin and Editor accounts.</p>

  return (
    <div className="space-y-6">
      <div>
        <Link to="/campaign-messages" className="text-sm text-primary hover:underline">← Campaign Messages</Link>
        <h1 className="mt-2 text-3xl font-bold text-foreground">Campaign Poll Results</h1>
        <p className="text-sm text-muted-foreground">Aggregate results only. Percentages use submitted respondents as the denominator.</p>
      </div>
      {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
      <div className="grid gap-4 lg:grid-cols-[minmax(250px,1fr)_minmax(350px,2fr)]">
        <Card>
          <CardHeader><CardTitle>Polls</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {loading ? <p className="text-sm text-muted-foreground">Loading polls…</p> : polls.length === 0 ? <p className="text-sm text-muted-foreground">No polls yet.</p> : null}
            {polls.map((campaign) => (
              <button
                type="button"
                key={campaign.id}
                onClick={() => setParams({ campaign: campaign.id })}
                className={`w-full rounded-md border p-3 text-left text-sm hover:bg-accent ${selectedId === campaign.id ? 'border-primary' : 'border-border'}`}
              >
                <span className="block font-semibold">{campaign.title}</span>
                <span className="block text-muted-foreground">{campaign.poll?.question}{(campaign.poll?.questions?.length ?? 0) > 1 ? ` · ${campaign.poll?.questions?.length} questions` : ''}{campaign.poll?.branch ? ' · branching' : ''}</span>
                <span className="block text-xs text-muted-foreground">{campaign.status} · closes {formatDate(campaign.poll?.closesAtUtc)}</span>
              </button>
            ))}
            {hasMore ? <Button variant="outline" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? 'Loading…' : 'Load older polls'}</Button> : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>{results ? (polls.find((poll) => poll.id === results.campaignId)?.title ?? results.question) : (selectedId ? 'Loading results…' : 'Select a poll')}</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            {results ? (
              <>
                <p className="text-sm text-muted-foreground">Opened {formatDate(results.openedAtUtc)} · closes {formatDate(results.closesAtUtc)}</p>
                <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                  <Metric label="Delivered" value={results.delivered} />
                  <Metric label="Opened while active" value={results.opened} />
                  <Metric label="Skipped" value={results.skipped} />
                  <Metric label="Respondents" value={results.respondents} />
                  <Metric label="Unanswered at close" value={results.unansweredAtClose} />
                </div>
                {(() => {
                  const questions = results.questions?.length ? results.questions : [{ id: 'single', question: results.question, allowsMultiple: results.allowsMultiple, respondents: results.respondents, options: results.options }]
                  const branchIndex = results.branch ? questions.findIndex((question) => question.id === results.branch?.questionId) : -1
                  return <div className="space-y-4">
                    {questions.slice(0, branchIndex >= 0 ? branchIndex + 1 : undefined).map((question, index) => (
                      <QuestionResultsCard key={question.id} question={question} heading={results.questions?.length ? `Question ${index + 1}: ${question.question}` : question.question} />
                    ))}
                    {branchIndex >= 0 && results.branch ? <div className="space-y-3 border-l-2 border-primary/40 pl-4">
                      <h3 className="text-sm font-semibold">Follow-up paths</h3>
                      {results.branch.routes.map((route) => {
                        const selectedOption = questions[branchIndex].options.find((option) => option.id === route.optionId)
                        const followUp = questions.find((question) => question.id === route.followUpQuestionId)
                        return <div key={route.optionId} className="space-y-2">
                          <p className="text-sm font-medium">After {selectedOption?.text ?? 'answer'} · {selectedOption?.votes ?? 0} completed responses</p>
                          {followUp ? <QuestionResultsCard question={followUp} heading={followUp.question} /> : <p className="rounded-md border border-border p-3 text-sm text-muted-foreground">Poll ends on this path.</p>}
                        </div>
                      })}
                    </div> : null}
                  </div>
                })()}
                <Button variant="outline" onClick={() => void getCampaignPollResults(results.campaignId).then(setResults).catch(() => setError('Could not refresh poll results.'))}>Refresh results</Button>
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return <div className="rounded-md border border-border p-3"><span className="block text-muted-foreground">{label}</span><strong className="text-xl text-foreground">{value}</strong></div>
}

type QuestionResult = NonNullable<CampaignPollResults['questions']>[number]

function QuestionResultsCard({ question, heading }: { question: QuestionResult; heading: string }) {
  return <div className="space-y-4 rounded-md border border-border p-4">
    <div><h3 className="font-semibold">{heading}</h3><p className="text-xs text-muted-foreground">{question.allowsMultiple ? 'Multiple choice' : 'Single choice'}{question.respondents !== undefined ? ` · ${question.respondents} respondents on this question` : ''}</p></div>
    {question.options.map((option) => <div key={option.id} className="space-y-1 text-sm">
      <div className="flex justify-between gap-3"><span>{option.text}</span><span className="font-semibold">{option.votes} · {(option.percentOfRespondents * 100).toFixed(1)}%</span></div>
      <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${Math.min(100, option.percentOfRespondents * 100)}%` }} /></div>
    </div>)}
  </div>
}
